import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';

import { CommonService } from '../shared-global/services/common.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  get BASE_URL() {
    return this.baseUrl;
  }

  ErrorMessages: { [key: number]: string } = {
    [HttpStatusCode.Unauthorized]:
      'You are not authorized to view this content. Please contact administrator!',
    [HttpStatusCode.Forbidden]:
      'You do not have permission to access this resource. Please contact administrator!',
    [HttpStatusCode.NotFound]: 'The requested resource could not be found!',
    [HttpStatusCode.InternalServerError]:
      'An unexpected error occurred. Please try again later!',
  };

  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    private spinner: NgxSpinnerService,
    @Inject('BASE_URL') private baseUrl: string
  ) {}

  private errorHandler(apiErr: any) {
    const severity = [
      HttpStatusCode.InternalServerError,
      HttpStatusCode.Unauthorized,
      HttpStatusCode.Forbidden,
    ].includes(apiErr.status)
      ? 'Error'
      : 'Warn';
    const errors = Object.values<string>(apiErr.error?.errors || { _: [''] });

    for (const errs of errors) {
      for (const err of errs || ['']) {
        this.commonService.showToast(
          severity,
          err ||
            this.ErrorMessages[apiErr.status] ||
            'An error occurred. Please try again later.',
          'Error'
        );
      }
    }
    throw apiErr;
  }

  get<T>(
    apiRoute: string,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body' | 'response';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
      withCredentials?: boolean;
      excludeErrorHandling?: boolean;
    }
  ) {
    this.spinner.show();

    return lastValueFrom(
      this.http.get(`${this.baseUrl}${apiRoute}`, options as any)
    )
      .catch((err) =>
        options?.excludeErrorHandling
          ? Promise.reject(err)
          : this.errorHandler(err)
      )
      .finally(() => this.spinner.hide()) as Promise<T>;
  }

  post<T>(
    apiRoute: string,
    body: any,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'blob' | 'json' | 'text';
      withCredentials?: boolean;
    }
  ) {
    this.spinner.show();

    return lastValueFrom(
      this.http.post(`${this.baseUrl}${apiRoute}`, body, options as any)
    )
      .catch((err) => this.errorHandler(err))
      .finally(() => this.spinner.hide()) as Promise<T>;
  }

  put<T>(
    apiRoute: string,
    body: any,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
    }
  ) {
    this.spinner.show();
    return lastValueFrom(
      this.http.put(`${this.baseUrl}${apiRoute}`, body, options)
    )
      .catch((err) => this.errorHandler(err))
      .finally(() => this.spinner.hide()) as Promise<T>;
  }

  patch<T>(
    apiRoute: string,
    body: any,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
      withCredentials?: boolean;
    }
  ) {
    this.spinner.show();
    return lastValueFrom(
      this.http.patch(`${this.baseUrl}${apiRoute}`, body, options as any)
    )
      .catch((err) => this.errorHandler(err))
      .finally(() => this.spinner.hide()) as Promise<T>;
  }

  delete<T>(
    apiRoute: string,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
    }
  ) {
    this.spinner.show();

    return lastValueFrom(
      this.http.delete(`${this.baseUrl}${apiRoute}`, options)
    )
      .catch((err) => this.errorHandler(err))
      .finally(() => this.spinner.hide()) as Promise<T>;
  }
}
